/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { defaultListingPageSize } from "Constants/BenefitConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { defaultVisualStudioBenefitFilterValues } from "Constants/VisualStudioBenefitConstants";

import { IVisualStudioBenefitState } from "./IVisualStudioBenefitState";
import {
  IDownloadRequests,
  IDownloadRequestsFail,
  IDownloadRequestsSuccess,
  ISearchVisualStudioBenefits,
  ISearchVisualStudioBenefitsFail,
  ISearchVisualStudioBenefitsSuccess,
  IUpdateCurrentPage,
  IUpdatePageSize,
  IVisualStudioBenefitsFilterValueAdd,
  IVisualStudioBenefitsFilterValueRemove,
  IVisualStudioBenefitsFilterValueUpdate,
  VisualStudioBenefitActionTypes,
} from "./VisualStudioBenefitAction";
import {
  DOWNLOAD_REQUESTS_TO_CSV,
  DOWNLOAD_REQUESTS_TO_CSV_CLEAN,
  DOWNLOAD_REQUESTS_TO_CSV_FAIL,
  DOWNLOAD_REQUESTS_TO_CSV_SUCCESS,
  UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_CURRENT_PAGE,
  UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_PAGE_SIZE,
  VISUAL_STUDIO_BENEFITS_FILTER_VALUE_ADD,
  VISUAL_STUDIO_BENEFITS_FILTER_VALUE_CLEAR,
  VISUAL_STUDIO_BENEFITS_FILTER_VALUE_REMOVE,
  VISUAL_STUDIO_BENEFITS_FILTER_VALUE_UPDATE,
  VISUAL_STUDIO_BENEFITS_SEARCH,
  VISUAL_STUDIO_BENEFITS_SEARCH_FAIL,
  VISUAL_STUDIO_BENEFITS_SEARCH_SUCCESS,
} from "./VisualStudioBenefitActionTypes";

const INITIAL_STATE: IVisualStudioBenefitState = {
  currentPage: 1,
  fileDownloadStatus: OperationStatus.Idle,
  fileDownloadErrorMessage: "",
  fileDownloadData: "",
  filteredCount: 0,
  isLoadingVisualStudioBenefits: false,
  loadVisualStudioBenefitsError: "",
  pageSize: defaultListingPageSize,
  searchText: "",
  visualStudioBenefits: [],
  visualStudioBenefitsFilterValues: defaultVisualStudioBenefitFilterValues,
};

const applyDownloadVisualStudioBenefitRequestsToCSV = (
  state: IVisualStudioBenefitState,
  action: IDownloadRequests
): IVisualStudioBenefitState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Pending,
});

const applyDownloadVisualStudioBenefitRequestsToCSVSuccess = (
  state: IVisualStudioBenefitState,
  action: IDownloadRequestsSuccess
): IVisualStudioBenefitState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Succeeded,
  fileDownloadData: action.csvFile,
});

const applyDownloadVisualStudioBenefitRequestsToCSVFailed = (
  state: IVisualStudioBenefitState,
  action: IDownloadRequestsFail
): IVisualStudioBenefitState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Failed,
  fileDownloadErrorMessage: action.errorMessage,
});

const applyDownloadApplicationsClean = (
  state: IVisualStudioBenefitState
): IVisualStudioBenefitState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Pending,
  fileDownloadData: "",
});

const applySearchVisualStudioBenefits = (
  state: IVisualStudioBenefitState,
  action: ISearchVisualStudioBenefits
): IVisualStudioBenefitState => ({
  ...state,
  visualStudioBenefits: [],
  isLoadingVisualStudioBenefits: true,
  searchText: action.searchText,
  currentPage: action.searchPage,
  loadVisualStudioBenefitsError: "",
});

const applySearchVisualStudioBenefitsSuccess = (
  state: IVisualStudioBenefitState,
  action: ISearchVisualStudioBenefitsSuccess
): IVisualStudioBenefitState => ({
  ...state,
  visualStudioBenefits: action.benefits,
  currentPage: action.searchPage,
  isLoadingVisualStudioBenefits: false,
  filteredCount: action.filteredCount,
  loadVisualStudioBenefitsError: "",
});

const applySearchVisualStudioBenefitsFail = (
  state: IVisualStudioBenefitState,
  action: ISearchVisualStudioBenefitsFail
): IVisualStudioBenefitState => ({
  ...state,
  loadVisualStudioBenefitsError: action.error,
  isLoadingVisualStudioBenefits: false,
});

const applyUpdateCurrentPage = (
  state: IVisualStudioBenefitState,
  action: IUpdateCurrentPage
): IVisualStudioBenefitState => ({
  ...state,
  currentPage: action.currentPage,
});

const applyUpdatePageSize = (
  state: IVisualStudioBenefitState,
  action: IUpdatePageSize
): IVisualStudioBenefitState => ({
  ...state,
  pageSize: action.pageSize,
});

const addVisualStudioBenefitsFilterValue = (
  state: IVisualStudioBenefitState,
  action: IVisualStudioBenefitsFilterValueAdd
): IVisualStudioBenefitState => ({
  ...state,
  visualStudioBenefitsFilterValues: state.visualStudioBenefitsFilterValues.concat([
    action.filterValue,
  ]),
});

const removeVisualStudioBenefitsFilterValue = (
  state: IVisualStudioBenefitState,
  action: IVisualStudioBenefitsFilterValueRemove
): IVisualStudioBenefitState => ({
  ...state,
  visualStudioBenefitsFilterValues: state.visualStudioBenefitsFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const updateVisualStudioBenefitsFilterValue = (
  state: IVisualStudioBenefitState,
  action: IVisualStudioBenefitsFilterValueUpdate
): IVisualStudioBenefitState => ({
  ...state,
  visualStudioBenefitsFilterValues: state.visualStudioBenefitsFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

const clearVisualStudioBenefitsFilterValue = (
  state: IVisualStudioBenefitState
): IVisualStudioBenefitState => ({
  ...state,
  visualStudioBenefitsFilterValues: [],
});

function visualStudioBenefitReducer(
  state = INITIAL_STATE,
  action: VisualStudioBenefitActionTypes
): IVisualStudioBenefitState {
  switch (action.type) {
    case VISUAL_STUDIO_BENEFITS_SEARCH: {
      return applySearchVisualStudioBenefits(state, action);
    }
    case VISUAL_STUDIO_BENEFITS_SEARCH_FAIL: {
      return applySearchVisualStudioBenefitsFail(state, action);
    }
    case VISUAL_STUDIO_BENEFITS_SEARCH_SUCCESS: {
      return applySearchVisualStudioBenefitsSuccess(state, action);
    }
    case UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_CURRENT_PAGE: {
      return applyUpdateCurrentPage(state, action);
    }
    case UPDATE_VISUAL_STUDIO_BENEFIT_LISTING_PAGE_SIZE: {
      return applyUpdatePageSize(state, action);
    }
    case VISUAL_STUDIO_BENEFITS_FILTER_VALUE_ADD: {
      return addVisualStudioBenefitsFilterValue(state, action);
    }
    case VISUAL_STUDIO_BENEFITS_FILTER_VALUE_CLEAR: {
      return clearVisualStudioBenefitsFilterValue(state);
    }
    case VISUAL_STUDIO_BENEFITS_FILTER_VALUE_REMOVE: {
      return removeVisualStudioBenefitsFilterValue(state, action);
    }
    case VISUAL_STUDIO_BENEFITS_FILTER_VALUE_UPDATE: {
      return updateVisualStudioBenefitsFilterValue(state, action);
    }
    case DOWNLOAD_REQUESTS_TO_CSV: {
      return applyDownloadVisualStudioBenefitRequestsToCSV(state, action);
    }
    case DOWNLOAD_REQUESTS_TO_CSV_SUCCESS: {
      return applyDownloadVisualStudioBenefitRequestsToCSVSuccess(state, action);
    }
    case DOWNLOAD_REQUESTS_TO_CSV_FAIL: {
      return applyDownloadVisualStudioBenefitRequestsToCSVFailed(state, action);
    }
    case DOWNLOAD_REQUESTS_TO_CSV_CLEAN: {
      return applyDownloadApplicationsClean(state);
    }
    default:
      return state;
  }
}

export default visualStudioBenefitReducer;
